import React, { useEffect, useState, useReducer } from 'react';
import { graphql, useStaticQuery } from 'gatsby';
import { Helmet } from 'react-helmet';
import Modal from 'react-modal';
import ReactPlayer from 'react-player';
import PropTypes from 'prop-types';
import { MarketoForm } from '@lucid/gatsby-theme-lucid';

import './layout.scss';

import whiteLogo from '../assets/images/logo-white.svg';
import xIcon from '../assets/images/consideration/x.svg';

import { useSiteMetadata } from '../hooks/use-site-metadata.ts';
import { getProcessedValue } from '../utils/helpers.ts';

import LayoutContext from '../contexts/layoutContext';
import AbTestContext from '../contexts/abTestContext';
import AbTest from './ab-test/ab-test.tsx';
import AbTestAdminUi from './ab-test-admin-ui/ab-test-admin-ui';

import { languages } from '../../data/languages.ts';
import phoneNumbers from '../../data/phoneNumbers.json';

import {
  startSecondsOnPage,
  recordSecondsOnPage,
  recordTemplate,
  recordLanguage
} from '../utils/vanityMetrics';
import '../stylesheets/_base.scss';
import '../stylesheets/_layout.scss';

import favicon from '../assets/images/favicon.png';

const Layout = ({
  langPrefix,
  marketoForms,
  location,
  pageType,
  children,
  metatags,
  otherTranslationData,
  nid,
  trackers
}) => {
  const {
    site: { pathPrefix }
  } = useStaticQuery(
    graphql`
      {
        site {
          pathPrefix
        }
      }
    `
  );

  const { siteUrl, title, cmsUrl } = useSiteMetadata();

  const [modal, setModal] = useState(null);
  const [videoModalUrl, setVideoModalUrl] = useState(null);
  const [videoAutoPlay, setVideoAutoPlay] = useState(false);
  const [videoConfig, setVideoConfig] = useState({});
  const [formData, setFormData] = useState({
    form: null,
    topText: null,
    confirm: null
  });

  const [abTests, updateTest] = useReducer((tempAbTests, abTest) => {
    const theseAbTests = Object.assign({}, tempAbTests);
    const { test } = abTest;
    const currentTest = tempAbTests[test] || {};

    Object.keys(abTest).forEach(key => {
      currentTest[key] = abTest[key];
    });

    theseAbTests[test] = currentTest;

    return theseAbTests;
  }, {});

  useEffect(() => {
    window.performance.mark('lucidLoadStart');
  }, []);

  const { form, topText, confirm } = formData;

  let showAbtestButtons = false;
  let showEditButton = false;

  if (typeof window !== 'undefined') {
    showEditButton = window.localStorage.getItem('showEditLink') === '1';
    showAbtestButtons = window.localStorage.getItem('showAbTestingUi') === '1';

    if (trackers) {
      const trackerKey = 'mktgTrackers';
      let existingTrackers = {};
      try {
        if (window.localStorage.getItem(trackerKey)) {
          existingTrackers = JSON.parse(
            window.localStorage.getItem(trackerKey)
          );
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }

      const newTrackers = Object.keys(trackers).reduce(
        (updatedTrackers, key) => {
          let fieldValue = trackers[key];
          if (fieldValue) {
            if (!Array.isArray(fieldValue)) {
              fieldValue = [fieldValue];
            }
            const newValues = fieldValue.map(value => value.entity.entityLabel);
            if (newValues) {
              // send to GA
              if (window.ga) {
                newValues.forEach(value => {
                  window.ga('send', {
                    eventCategory: trackerKey,
                    eventAction: key,
                    eventLabel: value
                  });
                });
              }

              // merge with existing values to be saved in local storage
              const existingValues = updatedTrackers[key] || [];
              const newSet = new Set([...newValues, ...existingValues]);
              // eslint-disable-next-line no-param-reassign
              updatedTrackers[key] = Array.from(newSet);
            }
          }
          return updatedTrackers;
        },
        existingTrackers
      );
      window.localStorage.setItem(trackerKey, JSON.stringify(newTrackers));
    }
  }

  const tagsWithName = ['description', 'keywords', 'robots'];

  const openModal = html => {
    setModal(html);
  };

  const closeModal = () => setModal(false);

  const getMarketoModal = (thisFormId, thisTopText = {}, thisConfirm = {}) => {
    const thisForm = marketoForms[thisFormId];

    if (!thisForm) {
      throw Error(
        `Did not find a form with ID ${thisFormId} of type ${typeof thisFormId}`
      );
    }

    setFormData({
      form: thisForm,
      topText: thisTopText,
      confirm: thisConfirm
    });
  };

  const getMarketoFields = (marketoForm, prefix) => {
    let mktoId = null;
    let mktoFields = {};
    if (
      !marketoForm ||
      !('entity' in marketoForm) ||
      !('entityTranslations' in marketoForm.entity)
    ) {
      return { mktoId, mktoFields };
    }
    const match = marketoForm.entity.entityTranslations.find(
      ({ entityLanguage }) => entityLanguage.id === prefix
    );

    if (match) {
      mktoId = match.fieldMarketoId;
      mktoFields = {
        title: match.fieldMarketoTitle,
        body: getProcessedValue(match.fieldMarketoBody),
        note: getProcessedValue(match.fieldMarketoNote)
      };
    }
    return { mktoId, mktoFields };
  };

  const closeMarketoModal = () =>
    setFormData({
      form: null,
      topText: null,
      confirm: null
    });

  const getVideoModal = (
    thisVideoModalUrl,
    autoPlay = false,
    thisVideoConfig = {}
  ) => {
    setVideoModalUrl(thisVideoModalUrl);
    setVideoAutoPlay(autoPlay);
    setVideoConfig(thisVideoConfig);
  };

  const { shortLangPrefix, i18nKey, urlLangPrefix } = languages[langPrefix];

  const layoutContext = {
    language: {
      langPrefix,
      shortLangPrefix,
      urlLangPrefix,
      i18nKey
    },
    location,
    marketo: {
      getMarketoModal,
      getMarketoFields
    },
    video: {
      getVideoModal
    },
    modal: {
      openModal
    },
    abTesting: {
      updateTest
    }
  };

  const metricLoggerHomepage = 'Homepage';
  const metricLoggerContact = 'Contact';
  const metricLoggerEnterprise = 'Enterprise';
  const metricLoggerEducation = 'Education';

  const metricLoggerPages = {
    en: {
      '/': metricLoggerHomepage,
      '/pages/home': metricLoggerHomepage,
      '/pages/contact': metricLoggerContact,
      '/pages/enterprise': metricLoggerEnterprise,
      '/pages/usecase/education': metricLoggerEducation
    },
    fr: {
      '/pages/fr': metricLoggerHomepage,
      '/pages/fr/contacter-lucidchart': metricLoggerContact,
      '/pages/fr/compte-entreprise-lucidchart': metricLoggerEnterprise,
      '/pages/fr/education': metricLoggerEducation
    },
    de: {
      '/pages/de': metricLoggerHomepage,
      '/pages/de/kontakt-zu-lucidchart': metricLoggerContact,
      '/pages/de/lucidchart-fur-unternehmen': metricLoggerEnterprise,
      '/pages/de/bildung': metricLoggerEducation
    },
    ja: {
      '/pages/ja': metricLoggerHomepage,
      '/pages/ja/enterprise': metricLoggerEnterprise,
      '/pages/ja/lucidchart-for-education': metricLoggerEducation
    },
    pt: {
      '/pages/pt': metricLoggerHomepage,
      '/pages/pt/entre-em-contato-com-o-lucidchart': metricLoggerContact,
      '/pages/pt/lucidchart-para-empresas': metricLoggerEnterprise,
      '/pages/pt/educacao': metricLoggerEducation
    },
    es: {
      '/pages/es': metricLoggerHomepage,
      '/pages/es/comunicate-con-lucidchart': metricLoggerContact,
      '/pages/es/lucidchart-para-empresas': metricLoggerEnterprise,
      '/pages/es/lucidchart-para-la-educacion': metricLoggerEducation
    },
    nl: {
      '/pages/nl': metricLoggerHomepage,
      '/pages/nl/contact-opnemen-met-lucidchart': metricLoggerContact,
      '/pages/nl/lucidchart-voor-ondernemingen': metricLoggerEnterprise,
      '/pages/nl/onderwijs': metricLoggerEducation
    }
  };

  useEffect(() => {
    const metricLoggerString =
      (metricLoggerPages[shortLangPrefix] || {})[window.location.pathname] ||
      'Other';

    const markLoaded = delayed => {
      const metricLogger = window.lucid.getMetricLogger(metricLoggerString);
      metricLogger.markLoaded();
      metricLogger.tag('path', window.location.pathname);
      metricLogger.tag('corporate', true);
      metricLogger.tag('delayed', delayed);
    };

    if (window.lucid && window.lucid.getMetricLogger) {
      markLoaded();
    } else {
      const checkInterval = setInterval(() => {
        if (window.lucid && window.lucid.getMetricLogger) {
          markLoaded(true);
          clearInterval(checkInterval);
        }
      }, 17);
    }

    startSecondsOnPage();
    recordTemplate(pageType === 'discovery' ? 'long-text' : pageType);
    recordLanguage(langPrefix);

    Modal.setAppElement('body');

    return () => {
      recordSecondsOnPage(location.pathname);
    };
  }, []);

  const updatePhoneNumbers = countryCode => {
    const replacePhoneNumbers = match => {
      const defaultNumbers = [
        '(888) 875-4552',
        '(888) 875-1867',
        '888-875-6970',
        '1 888-875-1683',
        '(877) 210-9890'
      ];
      defaultNumbers.forEach(defaultNumber => {
        if (match.innerText.includes(defaultNumber)) {
          /* eslint-disable-next-line */
          match.innerText = match.innerText.replace(
            defaultNumber,
            phoneNumbers[countryCode]
          );

          if (match.href) {
            /* eslint-disable-next-line */
            match.href = `tel:+${phoneNumbers[countryCode].replace(/ /g, '')}`;
          }
        }
      });
    };

    if (countryCode && phoneNumbers[countryCode]) {
      Array.from(document.querySelectorAll('.js-phone-number')).forEach(
        replacePhoneNumbers
      );
      Array.from(
        document.querySelectorAll('.js-phone-number-children > *')
      ).forEach(replacePhoneNumbers);
      setTimeout(() => {
        Array.from(
          document.querySelectorAll('.js-phone-number-children-async > *')
        ).forEach(replacePhoneNumbers);
      });
    }
  };

  const countryCodeKey = 'notInEuLoaderCountry';

  useEffect(() => {
    let interval;
    let countryCode = localStorage.getItem(countryCodeKey);
    const phoneClasses =
      '.js-phone-number,.js-phone-number-children,.js-phone-number-children-async';
    let hasPhoneNumbers = document.querySelectorAll(phoneClasses).length > 0;
    if (countryCode === null || !hasPhoneNumbers) {
      let iterations = 0;
      const CHECK_INTERVAL_MS = 500;
      const TIMES_TO_TRY = 20;

      interval = window.setInterval(() => {
        hasPhoneNumbers = document.querySelectorAll(phoneClasses).length > 0;
        countryCode = localStorage.getItem(countryCodeKey);
        if (countryCode !== null && hasPhoneNumbers) {
          updatePhoneNumbers(countryCode);
          clearInterval(interval);
        }
        iterations += 1;

        // Bail out after the first 20 tries
        if (iterations === TIMES_TO_TRY) {
          clearInterval(interval);
        }
      }, CHECK_INTERVAL_MS);
    } else {
      updatePhoneNumbers(countryCode);
    }

    return () => {
      clearInterval(interval);
    };
  }, [formData]);

  const handleCloseModal = e => {
    e.preventDefault();
    setVideoModalUrl('');
  };

  const siteSplitLogic = ({ branch }) => {
    if (
      typeof window !== 'undefined' &&
      window.localStorage.getItem('siteSplit') === null
    ) {
      localStorage.setItem('siteSplit', branch);
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'siteSplit',
        splitBranch: branch
      });
    }
  };

  let overrideCanonical = false;
  return (
    <>
      <Helmet>
        <title>{title}</title>

        {metatags.map(({ key, value }) => {
          let tag;
          if (key === 'title') {
            tag = <title key={key}>{value}</title>;
          } else if (key === 'canonical') {
            tag = <link key={key} rel={key} href={value} />;
            overrideCanonical = true;
          } else if (tagsWithName.indexOf(key) !== -1) {
            tag = <meta key={key} name={key} content={value} />;
          } else {
            tag = <meta key={key} property={key} content={value} />;
          }

          return tag;
        })}

        <meta
          name="ahrefs-site-verification"
          content="1d37e9df12edcf2469aa74004b83b3eaf4ddc051f47ee708d63c3dfdccf5e13d"
        />

        {!overrideCanonical && (
          <link rel="canonical" href={`${siteUrl}${location.pathname}`} />
        )}

        <link rel="shortcut icon" type="image/png" href={favicon} />

        <link
          rel="chrome-webstore-item"
          href="https://chrome.google.com/webstore/detail/apboafhkiegglekeafbckfjldecefkhn"
        />

        {otherTranslationData.map(({ entityUrl, entityLanguage }) => {
          let href = `${siteUrl}${pathPrefix}${entityUrl.path}`;

          if (entityLanguage.id === 'en' && pageType === 'homepage') {
            href = siteUrl;
          }

          return (
            <link
              key={entityLanguage.id}
              href={href}
              rel="alternate"
              hrefLang={entityLanguage.id}
            />
          );
        })}

        <html lang={shortLangPrefix} className={`pageType__${pageType}`} />
      </Helmet>
      <LayoutContext.Provider value={layoutContext}>
        {children}
        <AbTest test="siteSplit">
          <AbTestContext.Consumer>{siteSplitLogic}</AbTestContext.Consumer>
        </AbTest>
        {form !== null && (
          <Modal
            isOpen
            shouldCloseOnOverlayClick
            portalClassName="marketo-modal"
            overlayClassName="marketo-modal__overlay"
            className="marketo-modal__content"
            onRequestClose={closeMarketoModal}
          >
            <div className="marketo-modal__header">
              <img
                className="marketo-modal__logo"
                src={whiteLogo}
                alt="Lucidchart"
              />
              <button
                className="marketo-modal__close"
                type="button"
                onClick={closeMarketoModal}
              >
                ×
              </button>
            </div>
            <div className="marketo-modal__body">
              <MarketoForm
                form={form}
                topText={topText}
                confirm={confirm}
                location={location}
                i18nKey={i18nKey}
              />
            </div>
          </Modal>
        )}
        {videoModalUrl && (
          <Modal
            isOpen
            ariaHideApp={false}
            onRequestClose={e => handleCloseModal(e)}
            shouldCloseOnOverlayClick
            portalClassName="video-modal"
            overlayClassName="video-modal__overlay"
            className="video-modal__content"
          >
            <>
              <div className="video-modal__container">
                <ReactPlayer
                  url={videoModalUrl}
                  height="100%"
                  width="100%"
                  controls
                  playing={videoAutoPlay}
                  config={videoConfig}
                />
              </div>
              <button
                type="button"
                className="video-modal__button"
                tabIndex={0}
                onClick={e => handleCloseModal(e)}
                onKeyPress={e => handleCloseModal(e)}
              >
                <img
                  src={xIcon}
                  alt="close"
                  className="video-modal__exit"
                  width="20"
                />
              </button>
            </>
          </Modal>
        )}
        {modal && (
          <Modal
            isOpen
            ariaHideApp={false}
            onRequestClose={() => closeModal()}
            shouldCloseOnOverlayClick
            portalClassName="modal"
            overlayClassName="modal__overlay"
            className="modal__content"
          >
            <div className="modal__container">
              {modal}

              <button
                type="button"
                tabIndex={0}
                onClick={() => closeModal()}
                onKeyPress={() => closeModal()}
                className="modal__exit"
              />
            </div>
          </Modal>
        )}

        {showEditButton && (
          <a
            className="cms-edit-link"
            href={`${cmsUrl}/${urlLangPrefix}node/${nid}/edit`}
          >
            Edit Page
          </a>
        )}

        {showAbtestButtons && Object.keys(abTests).length > 0 && (
          <AbTestAdminUi abTests={abTests} />
        )}
      </LayoutContext.Provider>
    </>
  );
};

Layout.defaultProps = {
  location: {},
  otherTranslationData: [],
  metatags: [],
  marketoForms: {},
  nid: '',
  trackers: null
};

Layout.propTypes = {
  langPrefix: PropTypes.string.isRequired,
  otherTranslationData: PropTypes.arrayOf(PropTypes.object),
  metatags: PropTypes.arrayOf(PropTypes.object),
  location: PropTypes.shape({
    pathname: PropTypes.string,
    origin: PropTypes.string
  }),
  pageType: PropTypes.string.isRequired,
  marketoForms: PropTypes.shape({}),
  children: PropTypes.node.isRequired,
  nid: PropTypes.string,
  trackers: PropTypes.shape({})
};

export default Layout;

export const query = graphql`
  fragment i18nFile on SrcJson {
    parent {
      ... on File {
        name
      }
    }
  }

  fragment link on Drupal_ParagraphLink {
    fieldOpenNewTab
    fieldCta {
      url {
        path
      }
      title
    }
  }

  fragment marketo on Drupal_ParagraphMarketoForm {
    entityLanguage {
      id
    }
    fieldMarketoId
    fieldMarketoTitle
    fieldMarketoBody {
      processed
    }
    fieldMarketoNote {
      processed
    }
    fieldMarketoCtaText
  }

  fragment menuFragment on Drupal_MenuLinkContent {
    menuName
    parent
    entityUuid
    entityLanguage {
      id
    }
    title
    link {
      url {
        path
      }
    }
    ... on Drupal_MenuLinkContentFooter {
      fieldKmEventName
      fieldKey
      fieldMarketo {
        entity {
          ...marketo
        }
      }
    }
    ... on Drupal_MenuLinkContentDefaultFooter {
      fieldKmEventName
      fieldKey
      fieldMarketo {
        entity {
          ...marketo
        }
      }
    }
    ... on Drupal_MenuLinkContentMainNavLinks {
      fieldKmEventName
    }
    ... on Drupal_MenuLinkContentIntegrationPpcNavbar {
      fieldKey
      fieldKmEventName
    }
    ... on Drupal_MenuLinkContentMoreExamplesList {
      fieldTranslatedLink
      fieldCategoryImage
    }
  }
`;
